import * as apiDelete from 'api/erpRecipe/DeleteErpRecipeIdV1';
import * as apiPostSearch from 'api/erpRecipe/PostErpRecipeSearchV1';
import * as apiPost from 'api/erpRecipe/PostErpRecipeV1';
import * as apiPut from 'api/erpRecipe/PutErpRecipeIdV1';
import { Functor } from 'functions/Functor';
import * as StringList from 'types/Product/Search/StringList';
import * as Ingredient from 'types/Recipe/Detail/ErpNew/Ingredient';
import * as IngredientList from 'types/Recipe/Detail/ErpNew/IngredientList';
import * as OrderGuide from 'types/Recipe/Detail/ErpNew/OrderGuide';
import * as ProductList from 'types/Recipe/Detail/ErpNew/ProductList';

export enum ID {
	NEW = 'new',
	TEMPLATE = 'template',
}

export type Type = {
	id: ID | string;
	recipeId: string;
	name: string;
	ingredients: IngredientList.Type;
	isFavorite: boolean;
	lastEditedDateUtc: Date;
	createdDateUtc: Date;
	orderGuide: OrderGuide.Type;
};

export function create(): Type {
	return {
		id: ID.NEW,
		recipeId: '',
		name: '',
		isFavorite: false,
		lastEditedDateUtc: new Date(),
		createdDateUtc: new Date(),
		ingredients: IngredientList.create(),
		orderGuide: OrderGuide.create(),
	};
}

export function applyOrderGuidePriority(obj: Type): Type {
	return {
		...obj,
		ingredients: IngredientList.applyOrderGuidePriority(obj.ingredients, obj.orderGuide),
	};
}

export async function addOrderGuide(obj: Type): Promise<Type> {
	const orderGuide = await Functor.From(obj)
		.map(getAllIngredientIds)
		.map(StringList.distinct)
		.mapAsync(OrderGuide.getOrderGuideByIngredientIds)
		.get();
	return {
		...obj,
		orderGuide: orderGuide,
	};
}

export function toggleProductSelection(
	obj: Type,
	ingredientId: string,
	productId: string,
	segmentIndex: number
): Type {
	return {
		...obj,
		ingredients: IngredientList.toggleProductSelection(
			obj.ingredients,
			ingredientId,
			productId,
			segmentIndex
		),
	};
}

export function getAllIngredientIds(obj: Type): string[] {
	return obj.ingredients.map((e) => e.ingredientId);
}

export async function postToApi(obj: apiPost.Request): Promise<string> {
	const response = await apiPost.callApi(obj);
	return response.getOrDefault('');
}

export async function putToApi(id: string, obj: apiPut.Request): Promise<void> {
	await apiPut.callApi(id, obj);
}

export async function saveToApi(obj: Type): Promise<string> {
	const request = Functor.From(obj).map(clearObject).map(mapToApi).get();
	if (obj.id === ID.NEW) return await postToApi(request);
	await putToApi(obj.id, request);
	return obj.id;
}

function clearObject(obj: Type): Type {
	return {
		...obj,
		ingredients: IngredientList.getOnlySelectedProducts(obj.ingredients),
	};
}

function mapToApi(obj: Type): apiPost.Request {
	return {
		recipeId: obj.recipeId,
		name: obj.name,
		isFavorite: obj.isFavorite,
		lastEditedDateUtc: String(obj.lastEditedDateUtc),
		createdDateUtc: String(obj.createdDateUtc),
		mappings: IngredientList.mapToApi(obj.ingredients),
	};
}

export async function deleteFromApi(id: string): Promise<void> {
	await apiDelete.callApi(id);
}

export function setMappings(obj: Type, mappings: IngredientList.Type): Type {
	return {
		...obj,
		ingredients: mappings,
	};
}

export function mapFromApi(input: apiPostSearch.ErpRecipe): Type {
	return {
		...create(),
		...input,
		lastEditedDateUtc: new Date(input.lastEditedDateUtc ?? input.createdDateUtc),
		createdDateUtc: new Date(input.createdDateUtc),
		ingredients: IngredientList.mapFromApiSearch(input.mappings),
	};
}

export function mapFirstSuggestionToProduct(obj: Type): Type {
	return { ...obj, ingredients: IngredientList.mapFirstSuggestionToProduct(obj.ingredients) };
}

export function changeName(obj: Type, id: string, name: string): Type {
	if (obj.id !== id) return obj;
	return { ...obj, name: name };
}

export function mergeWithProducts(obj: Type, products: ProductList.Type): Type {
	return {
		...obj,
		ingredients: IngredientList.mergeWithProducts(obj.ingredients, products),
	};
}

export function mapFromApiSearch(e: apiPostSearch.ErpRecipe, recipeId: string): Type {
	return {
		id: e.id,
		recipeId: recipeId,
		name: e.name,
		isFavorite: e.isFavorite,
		lastEditedDateUtc: new Date(e.lastEditedDateUtc ?? e.createdDateUtc),
		createdDateUtc: new Date(e.createdDateUtc),
		ingredients: IngredientList.mapFromApiSearch(e.mappings),
		orderGuide: OrderGuide.create(),
	};
}

export function getAllProductIdsByIngredientId(obj: Type, ingredientId: string): string[] {
	const ingredient = obj.ingredients.find((e) => e.ingredientId === ingredientId);
	if (!ingredient) return [];
	return [
		...Ingredient.getAllProductIds(ingredient),
		...OrderGuide.getAllProductIdsFromIngredientId(obj.orderGuide, ingredient.ingredientId),
	];
}

export function addOrderGuideProducts(obj: Type, products: ProductList.Type): Type {
	return {
		...obj,
		ingredients: obj.ingredients.map((ingredient) => {
			const productIds = getAllProductIdsByIngredientId(obj, ingredient.ingredientId);
			const productsToAdd = products.filter((e) => productIds.includes(e.productId));
			return {
				...ingredient,
				products: ProductList.mergeTwoLists(ingredient.products, productsToAdd),
			};
		}),
	};
}

export function replaceProductsInIngredients(obj: Type, products: ProductList.Type): Type {
	return {
		...obj,
		ingredients: obj.ingredients.map((ingredient) => ({
			...ingredient,
			products: ingredient.products.map((product) => {
				const apiProduct = products.find((e) => e.productId === product.productId);
				if (apiProduct) return { ...apiProduct, selected: true };
				return product;
			}),
		})),
	};
}
export function applyIngredientIdToProducts(obj: Type): Type {
	return {
		...obj,
		ingredients: IngredientList.applyIngredientIdToProducts(obj.ingredients),
	};
}

export function sortProducts(obj: Type): Type {
	return { ...obj, ingredients: IngredientList.sortProducts(obj.ingredients) };
}

export function sortIngredients(obj: Type, sortOrder: { id: string; index: number }[]): Type {
	return { ...obj, ingredients: IngredientList.sortIngredients(obj.ingredients, sortOrder) };
}

export async function searchAndAddToManuallySearchedProductsToIngredient(
	obj: Type,
	ingredientId: string,
	searchTerm: string,
	nrOfMatches: number
): Promise<Type> {
	return {
		...obj,
		ingredients: await IngredientList.searchAndAddToManuallySearchedProducts(
			obj.ingredients,
			ingredientId,
			searchTerm,
			nrOfMatches,
			obj.orderGuide
		),
	};
}

export function resetManuallySearchedProductsFromIngredient(obj: Type, ingredientId: string): Type {
	return {
		...obj,
		ingredients: IngredientList.resetManuallySearchedProducts(obj.ingredients, ingredientId),
	};
}
